import { Timestamp } from "firebase/firestore/lite";

export enum TemplateParamSection {
  Header = "header",
  Data = "data",
}

export enum CampaignErrorField {
  Template = "template",
  EmailSubject = "emailSubject",
  EmailSender = "emailSender",
  TestEmail = "test_email",
  TestPhone = "test_phone",
}

export interface DbBusinessCampaign {
  id: string;
  name: string;
  description?: string;
  template_id: string;
  message?: string; // Optional for email campaigns
  template_params?: {
    key: string;
    value: string;
    name?: string;
    section?: TemplateParamSection; // optional for backward compatibility
  }[];
  media?: DbCampaignMedia[]; // Optional for email campaigns
  filter: CampaignFilter | null;
  notified_users: Array<{ client_id: string; created_at: Timestamp }>;
  status: DbCampaignStatus;
  channel: CampaignChannel;
  email_subject?: string; // Only for email campaigns
  email_sender?: string; // Verified sender email
  email_template?: string; // Full HTML with updated fields for preview and sending
  template_values?: Record<string, string>; // Stores filled values for editable fields
  created_at?: Timestamp;
  updated_at?: Timestamp;
}

export enum MediaType {
  Image = "image",
  Video = "video",
}

export enum FilterType {
  AllClients = "all_clients",
  NewClients = "new_clients",
  VisitedClients = "visited_clients",
}

export enum FilterDuration {
  Last30Days = "last_30_days",
  Last90Days = "last_90_days",
}

export interface DbCampaignMedia {
  url: string;
  media_id: string;
  blur_hash?: string;
  created_at: Timestamp;
  type: MediaType;
}

export enum CampaignMode {
  Test = "test",
  Send = "send",
}

export enum CampaignChannel {
  WhatsApp = "whatsapp",
  Email = "email",
}

export enum DbCampaignStatus {
  Draft = "draft",
  Sent = "sent",
}

export enum CampaignActionType {
  Add = "add",
  Copy = "copy",
  View = "view",
  Edit = "edit",
}

export interface Template {
  id: string;
  appId: string;
  category: string;
  containerMeta: string;
  data: string;
  elementName: string;
  meta: string;
  status: string;
  image?: string;
  templateType: "TEXT" | "IMAGE";
  vertical: string;
  wabaId: string;
  modifiedOn?: number;
}

export interface TemplateParam {
  key: string;
  value: string;
  name?: string;
  section?: TemplateParamSection; // optional for backward compatibility
}

export interface TemplatePreviewButton {
  type: "URL" | "PN";
  url: string;
  text: string;
}

export interface CampaignDetails {
  id: string | null;
  name: string;
  description: string;
  templateParams: TemplateParam[] | null;
  previewText: string;
  filter: CampaignFilter | null;
  notifyingClients:
    | {
        client_id: string;
        created_at: Timestamp;
      }[]
    | CampaignFilterClient[];
  media: DbCampaignMedia | null;
  createdAt: Timestamp | null;
  template: any | null;
  templateId: string | null;
  channel?: CampaignChannel;
  emailSubject?: string;
  emailSender?: string;
  templateValues?: Record<string, string>; // Stores filled values for editable fields
}

export interface CampaignFilter {
  type: "duration" | "custom";
  duration?: {
    value: number;
    unit: CampaignFilterDurationType;
  } | null;
  filter_start_date?: Timestamp | null;
  filter_end_date?: Timestamp | null;
  client_presence?: {
    new_clients?: boolean;
    not_booked_in_days?: boolean;
    loyal_clients?: {
      min_bookings_per_year?: number | null;
    };
    loyal_but_lost_clients?: {
      min_booking_duration: {
        value: number;
        unit: CampaignFilterDurationType;
      } | null;
      booking_frequency: number | null;
      lost_duration: {
        value: number;
        unit: CampaignFilterDurationType;
      } | null;
    };
  };
  service_usage?: {
    used_service_id?: string | null;
    not_used_service_id?: string | null;
    not_tried_service_id?: string | null;
    left_over_details?: {
      service_id?: string | null;
      booking_frequency: number | null;
      since_duration: {
        value?: number | null;
        unit?: CampaignFilterDurationType | null;
      } | null;
    } | null;
  };
  gender?: "male" | "female" | "all";
  amount_spent?: {
    more_than?: number | null;
    less_than?: number | null;
  };
}

export interface CampaignFilterClient {
  client_id: string;
  last_service_date?: Timestamp | null; // last service taken date
  total_spent: number; // amount spent by the client
  gender?: "male" | "female" | null; // gender filter
  onboarded_at: Timestamp; // user onboarding date
  services: string[]; // services used/not-used by the client
  used_services: ServiceRecord; // service not used by the client multiple times
  no_of_bookings_per_year: number; // loyal clients
  no_of_bookings: number; // loyal but lost clients
}

export interface ServiceRecord {
  service_id: string;
  booking_frequency: number;
  last_booked_at: Timestamp;
}

export enum CampaignFilterDurationType {
  Days = "days",
  Weeks = "weeks",
  Months = "months",
  Years = "years",
}

export interface EmailCampaignRequest {
  business_id: string;
  template_id: string;
  channel: CampaignChannel;
  clients: Array<{ id: string; email: string }>;
  mode: CampaignMode;
  campaign_id?: string; // Required only for Send mode
  email_sender?: string; // Optional, used in test mode
  email_subject?: string; // Optional, used in test mode
  template_values?: Record<string, string>; // Optional, used in test mode or if campaign has it
}
